import clsx from "clsx";
import { Link, LinkProps } from "./link";

type Variant =
  | "title-lg"
  | "title-base"
  | "title-sm"
  | "title-xs"
  | "label-lg"
  | "label-base"
  | "label-sm"
  | "label-xs"
  | "paragraph-lg"
  | "paragraph-base"
  | "paragraph-sm"
  | "paragraph-xs";

type Color = "primary" | "secondary" | "white";

interface TextProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLParagraphElement>,
    HTMLParagraphElement
  > {
  className?: string;
  variant?: Variant;
  color?: Color;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  "title-lg": "text-lg/7 font-bold",     // 18 px • lh 1.75 rem
  "title-base": "text-base/6 font-bold", // 16 px • lh 1.5 rem
  "title-sm": "text-sm/5 font-bold",     // 14 px • lh 1.25 rem
  "title-xs": "text-xs/4 font-bold",     // 12 px • lh 1 rem

  "label-lg": "text-lg/7 font-medium",     // 18 px • lh 1.75 rem
  "label-base": "text-base/6 font-medium", // 16 px • lh 1.5 rem
  "label-sm": "text-sm/5 font-medium",     // 14 px • lh 1.25 rem
  "label-xs": "text-xs/4 font-medium",     // 12 px • lh 1 rem

  "paragraph-lg": "text-lg/7",             // 18 px • lh 1.75 rem
  "paragraph-base": "text-base/6",         // 16 px • lh 1.5 rem
  "paragraph-sm": "text-sm/5",             // 14 px • lh 1.25 rem
  "paragraph-xs": "text-xs/4",             // 12 px • lh 1 rem
};

const COLOR_CLASSES: Record<Color, string> = {
  primary: "text-zinc-950 dark:text-white",
  secondary: "text-zinc-500 dark:text-zinc-400",
  white: "text-white",
};

export function Text({
  variant = "paragraph-sm",
  color = "secondary",
  className,
  ...props
}: TextProps) {
  return (
    <p
      data-slot="text"
      {...props}
      className={clsx(
        className,
        VARIANT_CLASSES[variant],
        COLOR_CLASSES[color],
      )}
    />
  );
}

interface TextProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLParagraphElement>,
    HTMLParagraphElement
  > {
  className?: string;
}

interface TextLinkProps extends LinkProps {
  className?: string;
}

export function TextLink({ className, ...props }: TextLinkProps) {
  return (
    <Link
      {...props}
      className={clsx(
        className,
        "!text-xs text-blue-500 underline decoration-blue-950/50 data-[hover]:decoration-blue-700 dark:text-blue-300 dark:decoration-blue-300 dark:data-[hover]:decoration-blue-400 cursor-pointer"
      )}
    />
  );
}

interface HtmlElementPropsWithClassName
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  > {
  className?: string;
}

export function Strong({ className, ...props }: HtmlElementPropsWithClassName) {
  return (
    <strong
      {...props}
      className={clsx(className, "font-medium text-zinc-950 dark:text-white")}
    />
  );
}

export function Code({ className, ...props }: HtmlElementPropsWithClassName) {
  return (
    <code
      {...props}
      className={clsx(
        className,
        "rounded border border-zinc-950/10 bg-zinc-950/[2.5%] px-0.5 text-sm font-medium text-zinc-950 sm:text-[0.8125rem] dark:border-white/20 dark:bg-white/5 dark:text-white"
      )}
    />
  );
}
